import {ConfigurationEvaluationContext} from './evaluation/configuration-evaluation-context/configuration-evaluation-context';
import {PropertiesEvaluator} from './evaluation/properties-evaluator/properties-evaluator';
import {IConfigurationData} from './configuration-data.interface';
import {Check} from '@sparkware/app-utils';
import {ServiceBase} from '../ServiceBase';
import {IConfigurationService} from './configuration.service.interface';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {LIVE_CASINO_ARENA_NAME, NEW_GAMES_ARENA_NAME, NEW_LIVE_CASINO_ARENA_NAME} from '../arenas/arena-consts';
import { observable, runInAction, makeObservable } from 'mobx';
import {ApplicationStartupConfigurations} from '../../app-bootstrap/config-interfaces/application-startup.configurations';
import {IConfigurationEvaluationDataProvider} from './evaluation/configuration-evaluation-context/configuration-evaluation-data-provider.interface';
import {injectAdminArenas} from './inject-admin-arenas';
import {HomePageHeaderArena} from "./config-data-sections/home-page.configuration";
import {IServicesConfigurations} from './config-data-sections/services-configurations';
import {ListConfiguration, SwiperGamesListConfiguration} from "./config-data-sections/arenas.configuration";


type GameTypesMap = {[key: number]: number};

export class ConfigurationsService extends ServiceBase implements IConfigurationService {

  startupConfiguration: ApplicationStartupConfigurations;

  constructor(services: IServiceFactoryExtended, startupConfigs: ApplicationStartupConfigurations) {
    super(services);

    makeObservable(this);

    this.startupConfiguration = startupConfigs;

    this._createConfigData();
  }

  private _getConfigEvaluationContextServices(): IConfigurationEvaluationDataProvider {
    return {
      admin: this.startupConfiguration.admin,
      configurationPath: this.startupConfiguration.configurationPath,
      getLauncherGames: () => this.services.launcher.getTestedGames(),
      getSegmentValue: (segmentName) => this.services.segmentation.getSegmentValue(segmentName),
      logger: this.services.logger
    };
  }

  private _createConfigData(): void {
    let originalConfigData = this.startupConfiguration.orbit.config;
    if (this.startupConfiguration.admin && this.startupConfiguration.admin.arenas) {
      injectAdminArenas(originalConfigData, this.startupConfiguration.admin.arenas);
    }

    let context = new ConfigurationEvaluationContext(this._getConfigEvaluationContextServices());
    let configEvaluator = PropertiesEvaluator.fromRoot(originalConfigData, context);
    let data = configEvaluator.evaluate(context);
    this._defineCustomConfigProperty(data,
      'allGamesTypes',
      () => {
        let gameTypes: number[] = [];
        let gamesTags = (data.generalSettings && data.generalSettings.gamesSettings && data.generalSettings.gamesSettings.gamesTags) || {};
        Object.getOwnPropertyNames(gamesTags).forEach(gt => {
          let gameType = parseInt(gt);
          if (!isNaN(gameType)) {
            gameTypes.push(gameType);
          }
        });
        return gameTypes;
      });


    runInAction(() => {
      this.data = data as IConfigurationData;
    });
  }


  private _defineCustomConfigProperty(configData: unknown, propertyName: string, evaluator: () => unknown) {
    Object.defineProperty(configData, propertyName, {
      get: evaluator,
      configurable: false
    });

  }

  @observable.ref
  data: IConfigurationData;

  get userSelectedState(): string {
    return this.startupConfiguration.userSelectedState;
  }

  get cmsPublicationId(): number {
    return this.startupConfiguration.b2c.publicationId;
  }

  get usedGameTypesBySite(): number[] {

    let gameTypesMap = {...this._getUsedGamesByHomePage(), ...this._getUsedGamesByArenas(), ...this._getUsedGameTypesByLiveGamesArena(), ...this._getUsedGameTypesByNewLiveGamesArena()};

    return Object.values(gameTypesMap);
  }

  private _getUsedGamesByHomePage(): GameTypesMap {
    let result: GameTypesMap = {};
    (this.data.homePage.gamesLists || []).forEach(gamesList => {
      if (gamesList.display) {
        let gamesIds = gamesList.gamesId || [];
        if (Check.isArray(gamesIds)) {
          gamesIds.forEach(gameType => {
            result[gameType] = gameType;
          })
        }

      }
    });

    return result;
  }

  private _getUsedGamesByArenas(): GameTypesMap {
    let result: GameTypesMap = {};
    Object.keys(this.data.arenas).forEach(arenaName => {
      let arenaConfig = this.data.arenas[arenaName];
      // game types from swipers
      (arenaConfig.swiperGamesList || []).forEach(swiperConfig => {
        if (swiperConfig.display) {
          let gamesIds = swiperConfig.gamesId || [];
          if (Check.isArray(gamesIds)) {
            gamesIds.forEach(gameType => {
              result[gameType] = gameType;
            });
          }

        }
      });

      // game types from all games grid
      (arenaConfig.allGamesGrid || []).forEach(gamesGridConfig => {
        if (gamesGridConfig.display) {
          let gamesIds = gamesGridConfig.gamesId || [];
          if (Check.isArray(gamesIds)) {
            gamesIds.forEach(gameType => {
              result[gameType] = gameType;
            });
          }
        }
      });
    });

    return result;
  }

  private _getUsedGameTypesByLiveGamesArena(): GameTypesMap {
    let result: GameTypesMap = {};

    let liveCasinoArenaConfig = this.data.arenas[LIVE_CASINO_ARENA_NAME];
    if (!liveCasinoArenaConfig) {
      return result;
    }

    let gamesIds = liveCasinoArenaConfig.arenas && liveCasinoArenaConfig.arenas.gamesId;

    if (Check.isArray(gamesIds)) {
      gamesIds.forEach(gameTypeConfig => {
        if (gameTypeConfig.display) {
          result[gameTypeConfig.gameId] = gameTypeConfig.gameId;
        }
      });
    }



    let popularTablesConfig = liveCasinoArenaConfig.popularTables;
    if (Check.isArray(popularTablesConfig)) {
      popularTablesConfig.forEach(tableConfig => {
        let tableGamesIds = tableConfig.gamesId || [];
        if (Check.isArray(tableGamesIds)) {
          tableGamesIds.forEach(gameType => result[gameType] = gameType);
        }
      });
    }

    return result;
  }

  private _getUsedGameTypesByNewLiveGamesArena(): GameTypesMap {
    let result: GameTypesMap = {};

    let liveCasinoArenaConfig = this.data.arenas[NEW_LIVE_CASINO_ARENA_NAME];
    if (!liveCasinoArenaConfig) {
      return result;
    }

    let gamesIds = liveCasinoArenaConfig.arenas && liveCasinoArenaConfig.arenas.gamesId;

    if (Check.isArray(gamesIds)) {
      gamesIds.forEach(gameTypeConfig => {
        if (gameTypeConfig.display) {
          result[gameTypeConfig.gameId] = gameTypeConfig.gameId;
        }
      });
    }



    let popularTablesConfig = liveCasinoArenaConfig.popularTables;
    if (Check.isArray(popularTablesConfig)) {
      popularTablesConfig.forEach(tableConfig => {
        let tableGamesIds = tableConfig.gamesId || [];
        if (Check.isArray(tableGamesIds)) {
          tableGamesIds.forEach(gameType => result[gameType] = gameType);
        }
      });
    }

    return result;
  }

  resolveConfigurationValue(fullPath: string): string {
    let objectValue: any = this.data;
    let fullPathArray = fullPath.split('.');

    for (let objectKey of fullPathArray) {
      objectValue = objectValue[objectKey];
    }

    return (objectValue as string) || '';
  }


  buildConfigUrl(fileName: string): string {
    let configurationPath = this.startupConfiguration.configurationPath;
    if (!configurationPath.endsWith('/')) {
      configurationPath += '/';
    }
    return configurationPath + fileName;
  }

  resolveGameImagePath(imageFileName: string): string {
    if (Check.isEmpty(imageFileName)) {
      return '';
    }

    let imagesBasePath = this.startupConfiguration.sdk.config.ExtraProperties.paths.gamesImages; //paths.gamesImages is used by cypress environment
    if (imagesBasePath) {
      if (!imagesBasePath.endsWith('/')) {
        imagesBasePath += '/';
      }
      return imagesBasePath + imageFileName;
    } else {
      let serverPath = this.startupConfiguration.sdk.config.ExtraProperties.paths.server;
      if (!serverPath.endsWith('/')) {
        serverPath += '/';
      }
      return serverPath + 'gamesImages/' + imageFileName;
    }
  }

  resolveJsFilePath(fileName: string): string {
    if (Check.isEmpty(fileName)) {
      return '';
    }
    let serverPath = this.startupConfiguration.sdk.config.ExtraProperties.paths.server;
    let path = serverPath ? serverPath : this.services.window.origin;
    if (!path.endsWith('/')) {
      path += '/';
    }
    return (path + `js/${fileName}`);
  }

  getFontsPath(): string {
    return new URL(this.startupConfiguration.sdk.config.ExtraProperties.paths.server + "/assets/fonts/").href;
  }

  getGamesListByJackpotId(id: string): SwiperGamesListConfiguration {

    const homePageGamesLists = this.data.homePage.gamesLists || [];
    for (let i = 0; i < homePageGamesLists.length; i++) {
      let gamesList = homePageGamesLists[i];
      if (gamesList.jackpotWidget?.jackpotId) {
        let gamesIds = gamesList.gamesId || [];
        if (Check.isArray(gamesIds) && gamesList.jackpotWidget.jackpotId.toString() === id) {
          return gamesList;
        }
      }
    }

    return null;
  }

  getSelfUpdatedByNewestGamesList(): ListConfiguration {
    const newGameArenaConfig = this.data.arenas[NEW_GAMES_ARENA_NAME];
    if (!newGameArenaConfig) {
      return null;
    }

    const gamesLists: ListConfiguration[] = (newGameArenaConfig.swiperGamesList || []).concat(newGameArenaConfig.allGamesGrid || []);
    const firstSelfUpdatedByNewest = gamesLists.find(listConfig => listConfig.display && listConfig.isSelfUpdatedByNewest);
    return firstSelfUpdatedByNewest;
  }

  private _getCurrentArenaLists() {
    const arenaType = this.services.routing.currentRoute.data?.arenaType;
    const lists = Check.isNullOrUndefined(arenaType) || arenaType === "homePage" ?
      this.data.homePage.gamesLists :
      this.services.configuration.data.arenas[arenaType].swiperGamesList;
    return lists;
  }

  getGamesListByTitle(title: string): SwiperGamesListConfiguration {
    const lists = this._getCurrentArenaLists();
    return lists?.find(list => list.title.toUpperCase() === title.toUpperCase());
  }

  getGamesListById(id: number): SwiperGamesListConfiguration {
    const lists = this._getCurrentArenaLists();
    return lists?.find(list => list.listId === id);
  }

  getHomePageHeaderArenas(): HomePageHeaderArena[] {
    let homePageHeaderArenas: HomePageHeaderArena[] = [];
    this.data.homePage.headerArenasOrder.forEach(name => {
      const arenaHeader = this.data.arenas[name].homepageHeader;
      if (arenaHeader) {
        homePageHeaderArenas.push(arenaHeader);
      }
    });
    return homePageHeaderArenas;
  }

  getHomePageHeaderArenasByUserOrientation(arenaNames: string[]): HomePageHeaderArena[] {
    let orderedArenas: HomePageHeaderArena[] = [];
    arenaNames.forEach(arenaName => {
      const arenaConfig: HomePageHeaderArena = this.data.arenas[arenaName] && this.data.arenas[arenaName].homepageHeader;
      if (arenaConfig) {
        orderedArenas.push(arenaConfig);
      }
    });
    return orderedArenas;
  }

  get servicesConfigs(): IServicesConfigurations {
    return this.services.sdk.getConfigs();
  }
}
